import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../../../api/axiosConfig'; // Import the configured Axios instance
import ImageViewer from './ImageViewer';
import { toast } from 'react-toastify';
import ForwardMessageModal from './ForwardMessageModal'; // Import the modal
import AudioPreview from './AudioPreview'; // Import the new AudioPreview component
import { MicrophoneIcon } from '@heroicons/react/24/solid'; // Import MicrophoneIcon
import {
    MessageList,
    Message,
    MessageInput, // Removed MessageInput import
    Avatar,
    ConversationHeader,
    MessageSeparator
} from '@chatscope/chat-ui-kit-react';
import { Link } from 'react-router-dom';
import { ChevronUpIcon, ChevronDownIcon, Bars3Icon, PaperAirplaneIcon, PaperClipIcon, ArrowUturnLeftIcon, XMarkIcon, ArrowRightIcon } from '@heroicons/react/24/solid'; // Import icons including ArrowUturnLeftIcon, XMarkIcon, and ArrowRightIcon
import { Chat, WhatsAppMessage, PLACEHOLDER_AVATAR_SVG, SendMessagePayload, VCardContact } from '../types';
import { renderMessageContent, MessageStatusIndicator, formatLastSeen, isDifferentDay, getMessagePosition, createVCard, formatPhoneNumber } from '../utils';
import { getAudioFilenameExtension, getAudioRecordingError } from '../audioRecording';
import ReplyPreview from './ReplyPreview'; // Import ReplyPreview

// Define ClientStats interface matching the backend response (or import if defined elsewhere)
interface ClientStats {
    totalSpent: number;
    totalOrderCount: number;
    activeOrderCount: number;
    latestCompletionDate: string | null;
    totalPaid: number;
    totalOwed: number;
    quoteCount: number; // Assuming quoteCount is still returned
    averageOrderValue: number;
    firstOrderDate: string | null;
    latestOrderDate: string | null;
    responsiblePersons?: string[] | null; // Changed to array for multiple responsible persons
    error?: string; // Optional error field
}

// Define the extended stats interface including the name
interface ClientStatsWithName extends ClientStats {
    name: string;
}


// Extend Chat interface locally or import if defined elsewhere
interface EnrichedChat extends Chat {
    phone?: string;
    clientId?: string; // Keep first client ID for potential primary actions/linking
    clientIds?: string[]; // Store all associated client IDs
    clientNames?: string[]; // Store all associated client names
    isKnownClient?: boolean;
    contactName?: string; // Added contact person field (from first client)
}

interface ChatWindowProps {
    activeChat: EnrichedChat; // Use enriched type
    messages: WhatsAppMessage[];
    isLoadingHistory: boolean;
    presenceStatus?: string;
    lastSeen?: number;
    messageInputValueParent: string;
    onMessageChangeParent: (value: string) => void;
    // Update onSendMessage to accept the unified payload
    onSendMessage: (payload: SendMessagePayload) => void;
    onAttachClick: () => void;
    fileInputRef: React.RefObject<HTMLInputElement | null>;
    // Update onFileChange to accept optional quotedId
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>, quotedMessageId?: string) => void;
    // Update prop to accept the map
    activeClientStatsMap: { [clientId: string]: ClientStatsWithName } | null;
    isLoadingStats: boolean;
    onLoadHistory: () => void;
    onRequestMedia: (messageId: string) => void;
    isMobileSidebarVisible: boolean; // Prop to know if sidebar is visible on mobile
    toggleMobileSidebarVisibility: () => void; // Function to toggle sidebar visibility
    // onReplyToMessage prop is removed as state is now internal
    chats: Chat[]; // Add chats prop for the forward modal
    onLoadAllHistoryAndScroll: (messageId: string) => void; // New prop
    scrollToMessageId?: string | null; // New prop
    onQuotedMessageScrolled: () => void; // New prop to reset scrollToMessageId
    // Contact action handlers
    onCallContact?: (phone: string) => void;
    onMessageContact?: (phone: string) => void;
    onEmailContact?: (email: string) => void;
    onSaveContact?: (contact: VCardContact) => void;
    onViewContact?: (contact: VCardContact) => void;
    onCreateChat?: (phoneNumber: string, name?: string) => void;
}


// Simple debounce function
const debounce = <F extends (...args: any[]) => any>(func: F, waitFor: number) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    return (...args: Parameters<F>): void => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            func(...args);
        }, waitFor);
    };
};

const ArchiveOnlyBadge: React.FC<{ message: WhatsAppMessage }> = ({ message }) => {
    if (!message.isArchivedOnly) return null;

    return (
        <span
            className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
            title="Сообщение сохранено в архиве портала, но отсутствует в текущей истории WhatsApp"
        >
            Только в архиве
        </span>
    );
};

const ChatWindow: React.FC<ChatWindowProps> = ({
    activeChat,
    messages,
    isLoadingHistory,
    presenceStatus,
    lastSeen,
    messageInputValueParent,
    onMessageChangeParent,
    onSendMessage,
    onAttachClick,
    fileInputRef,
    onFileChange,
    activeClientStatsMap, // Use the new prop name
    isLoadingStats,
    onLoadHistory,
    onRequestMedia,
    isMobileSidebarVisible, // Destructure new prop
    toggleMobileSidebarVisibility, // Destructure new prop
    // onReplyToMessage is removed
    chats, // Destructure chats prop
    onLoadAllHistoryAndScroll, // Destructure new prop
    scrollToMessageId, // Destructure new prop
    onQuotedMessageScrolled,
        onCallContact,
    onMessageContact,
    onEmailContact,
    onSaveContact,
    onViewContact,
    onCreateChat
}) => {
    const [isStatsCollapsed, setIsStatsCollapsed] = useState(false);
    const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null); // State for hovered message
    const [replyingToMessage, setReplyingToMessage] = useState<WhatsAppMessage | null>(null); // State for reply context moved here
    const [forwardingMessage, setForwardingMessage] = useState<WhatsAppMessage | null>(null); // State for message to forward
    const [isForwardModalOpen, setIsForwardModalOpen] = useState(false); // State for forward modal visibility
    const messageListRef = useRef<HTMLDivElement>(null);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Ref to manage scroll timeout for auto-scroll prevention
    const isNearBottomRef = useRef(true); // Ref to track if user was near bottom before loading history

    // Image viewer state
    const [imageViewerOpen, setImageViewerOpen] = useState(false);
    const [viewerImageUrl, setViewerImageUrl] = useState('');
    const [viewerImageCaption, setViewerImageCaption] = useState<string | undefined>(undefined);
    const [viewerImageFilename, setViewerImageFilename] = useState<string | undefined>(undefined);
    const textareaRef = useRef<HTMLTextAreaElement>(null); // Ref for textarea
    const lastTextSendRef = useRef<{ key: string; timestamp: number } | null>(null);
    const [messageInputValue, setMessageInputValueState] = useState(''); // Renamed state setter for clarity

    // Voice Message State
    const [isRecording, setIsRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
    const [audioPreviewUrl, setAudioPreviewUrl] = useState<string | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const recordingStartedAtRef = useRef<number | null>(null);
    const recordingStartPendingRef = useRef(false);
    const stopRecordingAfterStartRef = useRef(false);

    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

    const toggleStatsCollapse = () => setIsStatsCollapsed(!isStatsCollapsed); // Function to toggle stats collapse

const handleCreateChat = (phoneNumber: string, name?: string) => {
        if (onCreateChat) {
            onCreateChat(phoneNumber, name);
        } else {
            console.log('Create chat with:', phoneNumber, name);
            toast.info(`Создание чата с ${name || phoneNumber} будет реализовано`);
        }
    };



const handleCallContact = (phone: string) => {
        if (onCallContact) {
            onCallContact(phone);
        } else {
            window.open(`tel:${phone}`, '_blank');
        }
    };

    const handleMessageContact = (phone: string) => {
        if (onMessageContact) {
            onMessageContact(phone);
        } else {
            console.log('Start new chat with:', phone);
            if (window.Notification && Notification.permission === 'granted') {
                new Notification(`Создать чат с ${phone}`, {
                    body: 'Функция создания нового чата будет реализована'
                });
            }
        }
    };

    const handleEmailContact = (email: string) => {
        if (onEmailContact) {
            onEmailContact(email);
        } else {
            window.open(`mailto:${email}`, '_blank');
        }
    };

    const handleSaveContact = (contact: VCardContact) => {
        if (onSaveContact) {
            onSaveContact(contact);
        } else {
            const vcard = createVCard(contact);
            const blob = new Blob([vcard], { type: 'text/vcard' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${contact.name || 'contact'}.vcf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            
            toast.success(`Контакт ${contact.name || ''} сохранен как vCard файл`);
        }
    };

    const handleViewContact = (contact: VCardContact) => {
        if (onViewContact) {
            onViewContact(contact);
        } else {
            const details = [
                contact.name && `Имя: ${contact.name}`,
                contact.phone && `Телефон: ${formatPhoneNumber(contact.phone)}`,
                contact.email && `Email: ${contact.email}`,
                contact.org && `Организация: ${contact.org}`,
                contact.title && `Должность: ${contact.title}`
            ].filter(Boolean).join('\n');
            
            if (details) {
                alert(details);
            } else {
                alert('Нет информации о контакте');
            }
        }
    };

    const contactHandlers = {
        onCall: handleCallContact,
        onMessage: handleCreateChat,
        onEmail: handleEmailContact,
        onSaveContact: handleSaveContact,
        onViewContact: handleViewContact,
        onCreateChat: handleCreateChat
    };

    // Handler for opening image viewer
    const handleImageView = (imageUrl: string, caption?: string, filename?: string) => {
        setViewerImageUrl(imageUrl);
        setViewerImageCaption(caption);
        setViewerImageFilename(filename);
        setImageViewerOpen(true);
    };

    // Debounced version of onLoadHistory
    const debouncedLoadHistory = useCallback(debounce(onLoadHistory, 300), [onLoadHistory]); // Adjust debounce time as needed

    // Scroll handler to load more messages - expects native Event when used with addEventListener
    const handleScroll = useCallback((event: Event) => { // Changed type to native Event
        // Use event.currentTarget, but cast it to HTMLElement to access scroll properties
        try {
            const element = event.currentTarget as HTMLElement;
            if (element) {
                const { scrollTop } = element;
                // Log scroll position frequently when near the top for debugging
                if (scrollTop < 50) {
                    console.log(`[Scroll Debug] scrollTop: ${scrollTop}, isLoadingHistory: ${isLoadingHistory}`);
                }

                // Check if ready to load history
                if (!isLoadingHistory) {
                    // Trigger load more when scrolled very close to the top (e.g., less than 5px)
                    if (scrollTop < 5) {
                        console.log(`[Scroll Trigger] Near top (scrollTop: ${scrollTop}), attempting to load history...`);
                        // Store scroll position before loading new messages
                        const currentScrollHeight = element.scrollHeight;
                        const currentScrollTop = element.scrollTop;

                        debouncedLoadHistory(); // Call the debounced function passed from parent

                        // After loading, try to restore scroll position relative to old top message
                        // This needs coordination with the parent component knowing when loading finishes
                        // For now, we just trigger the load. Parent needs to handle scroll restoration logic if desired.

                    }
                } else {
                    // Log if scroll happens near top but loading is already in progress
                    if (scrollTop < 5) {
                        console.log(`[Scroll Debug] Near top (scrollTop: ${scrollTop}), but history is already loading.`);
                    }
                }


                // Update isNearBottomRef based on current scrcs-message-listll position
                const { scrollHeight, clientHeight } = element;
                isNearBottomRef.current = scrollHeight - scrollTop - clientHeight < 150; // Check if near bottom (e.g., within 150px)

                // Clear any existing scroll timeout if user scrolls manually
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                    scrollTimeoutRef.current = null;
                }
            }
        } catch (error) {
            console.error("Error in handleScroll:", error);
        }
    }, [isLoadingHistory, debouncedLoadHistory]);

    // Effect to attach scroll listener to the inner scrollable element
    useEffect(() => {
        const outerDivElement = messageListRef.current;
        if (!outerDivElement) {
            console.log('[Effect] Outer div element (messageListRef) not found.');
            return;
        }
        
        console.log('[Effect] Found outer div element.');
        
        const scrollWrapper = outerDivElement.querySelector('.cs-message-list__scroll-wrapper') as HTMLElement | null;

        if (scrollWrapper) {
            console.log('[Effect] Found .cs-message-list__scroll-wrapper, attaching scroll listener.');
            scrollWrapper.addEventListener('scroll', handleScroll);
            
            return () => {
                console.log('[Effect] Cleaning up scroll listener from scrollWrapper.');
                scrollWrapper.removeEventListener('scroll', handleScroll);
            };
        } else {
            console.log('[Effect] Could not find .cs-message-list__scroll-wrapper, trying alternative selectors.');
            
            const alternativeSelectors = [
                '.cs-message-list', 
                '.message-list', 
                '[data-scrollable="true"]'
            ];
            
            let foundElement: HTMLElement | null = null;
            for (const selector of alternativeSelectors) {
                foundElement = outerDivElement.querySelector(selector) as HTMLElement;
                if (foundElement) {
                    console.log(`[Effect] Found element with selector ${selector}, attaching scroll listener.`);
                    foundElement.addEventListener('scroll', handleScroll);
                    break;
                }
            }
            
            if (!foundElement) {
                console.log('[Effect] No scrollable element found, attaching listener to outer div as fallback.');
                outerDivElement.addEventListener('scroll', handleScroll);
            }
            
            return () => {
                if (foundElement) {
                    foundElement.removeEventListener('scroll', handleScroll);
                } else {
                    outerDivElement.removeEventListener('scroll', handleScroll);
                }
            };
        }
    }, [handleScroll, activeChat.id]); // Re-run effect if handleScroll OR activeChat changes

    // Scroll to bottom logic
    useEffect(() => {
        const messageListElement = messageListRef.current;
        if (messageListElement) {
            // Only auto-scroll if the user was near the bottom before the update
            if (isNearBottomRef.current && !isLoadingHistory) {
                // Clear previous timeout if exists
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }
                // Set a new timeout to scroll after a short delay
                scrollTimeoutRef.current = setTimeout(() => {
                    if (messageListElement) {
                        messageListElement.scrollTop = messageListElement.scrollHeight;
                    }
                    scrollTimeoutRef.current = null; // Clear ref after execution
                }, 150); // Delay allows DOM to update
            }
            // Reset near bottom ref for next update cycle if needed,
            // but generally it's updated by the scroll handler.
        }
        
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, [messages, activeChat.id, isLoadingHistory]); // Rerun when messages, chat, or loading state change

    // Effect to scroll to a specific message ID when `scrollToMessageId` prop changes
    useEffect(() => {
        if (!scrollToMessageId || !messageListRef.current) return;
        
        const messageElement = document.getElementById(`message-item-${scrollToMessageId}`);
        if (messageElement) {
            console.log(`[ChatWindow] Scrolling to message ID: ${scrollToMessageId}`);
            messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            messageElement.classList.add('bg-yellow-200', 'dark:bg-yellow-700', 'transition-all', 'duration-1000');
            setTimeout(() => {
                messageElement.classList.remove('bg-yellow-200', 'dark:bg-yellow-700');
            }, 2000);
            
            onQuotedMessageScrolled();
        } else {
            console.warn(`[ChatWindow] Message element with ID ${scrollToMessageId} not found for scrolling.`);
        }
    }, [scrollToMessageId, messages, onQuotedMessageScrolled]); // Depend on scrollToMessageId and messages

    // Adjust textarea height based on content
    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            requestAnimationFrame(() => {
                textarea.style.height = 'auto'; // Reset height
                const scrollHeight = textarea.scrollHeight;
                // Set a max height (e.g., 150px)
                const maxHeight = 150;
                textarea.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
                // Enable scroll if content exceeds max height
                textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
            });
        }
    }, [messageInputValue]); // Adjust height when input value changes

    // Paste handler to strip formatting
    const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        event.preventDefault(); // Prevent default paste behavior
        const text = event.clipboardData.getData('text/plain'); // Get plain text
        const target = event.target as HTMLTextAreaElement;
        const start = target.selectionStart;
        const end = target.selectionEnd;
        const currentValue = target.value;
        const newValue = currentValue.substring(0, start) + text + currentValue.substring(end);
        setMessageInputValueState(newValue); // Update state with plain text

        // Move cursor to the end of pasted text - needs slight delay
        requestAnimationFrame(() => {
            target.selectionStart = target.selectionEnd = start + text.length;
        });
    };

    // Handle Enter key for sending, Shift+Enter for newline
    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault(); // Prevent newline on Enter
            handleSendText(); // Use the correct handler for sending text
        }
        // Shift+Enter will naturally create a newline
    };

    // Handle initiating a reply (now internal to ChatWindow)
    const handleInitiateReply = (message: WhatsAppMessage) => {
        console.log(`[ChatWindow] Initiating reply to message ID: ${message.id}`);
        setReplyingToMessage(message);
        textareaRef.current?.focus(); // Focus the textarea when reply starts
    };

    // Handle initiating a forward
    const handleInitiateForward = (message: WhatsAppMessage) => {
        console.log(`[ChatWindow] Initiating forward for message ID: ${message.id}`);
        setForwardingMessage(message);
        setIsForwardModalOpen(true);
    };

    // Handle confirming the forward action (called from modal)
    const handleConfirmForward = async (targetChatId: string) => {
        if (!forwardingMessage) return;

        console.log(`[ChatWindow] Forwarding message ${forwardingMessage.id} to chat ${targetChatId}`);
        setIsForwardModalOpen(false); // Close modal immediately

        try {
            console.log(`[API Call] Forwarding message ${forwardingMessage.id} to chat ${targetChatId}`);
            const response = await api.post('/whatsapp/forward', { // Use relative path '/whatsapp/forward'
                originalMessageId: forwardingMessage.id,
                targetChatId: targetChatId,
            });

            if (response.status === 200) {
                // Find the target chat name for a better toast message
                const targetChat = chats.find(chat => chat.id === targetChatId);
                const targetChatName = targetChat ? targetChat.name : targetChatId;
                toast.success(`Сообщение переслано в чат "${targetChatName}"`);
                setForwardingMessage(null); // Clear forwarding state on success
            } else {
                // This case might not be reached if axios throws for non-2xx status
                console.error("Forward message API call failed with status:", response.status, response.data);
                toast.error(response.data?.message || "Не удалось переслать сообщение (серверная ошибка).");
            }
        } catch (error: any) { // Catch Axios errors
            console.error("Failed to forward message:", error);
            const errorMessage = error.response?.data?.message || error.message || "Не удалось переслать сообщение (ошибка сети или сервера).";
            toast.error(errorMessage);
            // Keep modal open or forwarding state? Close and show error is usually better UX.
            setForwardingMessage(null); // Clear state even on error to avoid keeping the modal stuck
        }
    };


    // Handle sending the text message
    const handleSendText = () => {
        const trimmedText = messageInputValue.trim();
        if (trimmedText) {
            const sendKey = `${activeChat.id}|${activeChat.phone || ''}|${replyingToMessage?.id || ''}|${trimmedText}`;
            const now = Date.now();
            if (lastTextSendRef.current?.key === sendKey && now - lastTextSendRef.current.timestamp < 1500) {
                console.warn('[ChatWindow] Ignoring duplicate text send click.');
                return;
            }
            lastTextSendRef.current = { key: sendKey, timestamp: now };

            const payload: SendMessagePayload = {
                chatId: activeChat.id,
                phone: activeChat.phone,
                message: trimmedText,
                media: null,
                quotedMessageId: replyingToMessage?.id
            };
            onSendMessage(payload); // Send the payload object
            setMessageInputValueState(''); // Clear internal state
            onMessageChangeParent(''); // Also clear parent state if needed
            setReplyingToMessage(null); // Clear reply state
        }
    };

    // Handle sending the recorded audio message
    const handleSendAudio = () => {
        if (audioBlob) {
            const audioError = getAudioRecordingError(audioBlob.size);
            if (audioError) {
                toast.error(audioError);
                handleCancelAudio();
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                const base64Data = (reader.result as string)?.split(',')[1]; // Extract base64 data
                if (base64Data) {
                    const fileExtension = getAudioFilenameExtension(audioBlob.type);
                    console.log(`Sending audio: MimeType=${audioBlob.type}, Size=${audioBlob.size}`);
                    // Construct the payload for the socket event
                    const payload = {
                        chatId: activeChat.id,
                        phone: activeChat.phone,
                        message: '', // No caption for voice message
                        media: {
                            mimetype: audioBlob.type || 'audio/ogg', // Default mimetype if needed
                            data: base64Data,
                            filename: `voice_message_${Date.now()}.${fileExtension}` // Keep filename consistent with the recorded container
                        },
                        // Check whatsapp-web.js docs for sending as voice note explicitly
                        // options: { sendAudioAsVoice: true } // Example if option exists
                        quotedMessageId: replyingToMessage?.id // Include if replying
                    };

                    // TODO: Replace this alert with the actual socket emission logic.
                    // This likely involves calling a prop function passed from the parent
                    // Send the payload using the updated onSendMessage prop
                    onSendMessage(payload);

                    handleCancelAudio(); // Clear preview after sending
                    setReplyingToMessage(null); // Clear reply state if audio was sent in reply context
                } else {
                    console.error("Failed to convert audio blob to base64.");
                    toast.error("Ошибка при обработке аудио."); // Use toast for errors
                }
            };
            reader.onerror = (error) => {
                console.error("FileReader error:", error);
                toast.error("Ошибка при чтении аудиофайла."); // Use toast for errors
            };
            reader.readAsDataURL(audioBlob); // Read as Data URL to get base64
        }
    };

    // Handle cancelling the audio preview
    const handleCancelAudio = () => {
        setAudioBlob(null);
        if (audioPreviewUrl) {
            URL.revokeObjectURL(audioPreviewUrl); // Clean up object URL
        }
        setAudioPreviewUrl(null);
        audioChunksRef.current = []; // Clear chunks
    };

    // Handle changing a message
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const pointer = e.target.selectionStart;
        const element = e.target;
        const text = e.target.value;
        window.requestAnimationFrame(() => {
            element.selectionStart = pointer;
            element.selectionEnd = pointer;
        });
        setMessageInputValueState(text); // Update internal state
    };

    // Sync internal state with parent prop if needed (e.g., for external updates)
    useEffect(() => {
        if (messageInputValueParent !== messageInputValue && document.activeElement !== textareaRef.current) {
            setMessageInputValueState(messageInputValueParent);
        }
    }, [messageInputValueParent]);

    useEffect(() => {
        // Set parent value from prop
        if (document.activeElement != textareaRef.current && messageInputValueParent != messageInputValue) {
            onMessageChangeParent(messageInputValue);
        }
    }, [messageInputValue]); // Update when  value changes


    // Handle file change and sending
    const handleFileSend = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || event.target.files.length === 0) return;
        
        const files = Array.from(event.target.files);
        setSelectedFiles(files);
        
        console.log(`📁 ChatWindow: Selected ${files.length} files`);
        
        if (files.length > 1) {
            toast.info(`Отправка ${files.length} файлов...`);
        }
        
        onFileChange(event, replyingToMessage?.id);
        setReplyingToMessage(null);
        
        setTimeout(() => setSelectedFiles([]), 3000);
    };


    // --- Voice Recording Handlers ---

    const startRecording = async () => {
        if (recordingStartPendingRef.current || mediaRecorderRef.current?.state === 'recording') {
            return;
        }

        recordingStartPendingRef.current = true;
        stopRecordingAfterStartRef.current = false;

        // Clear any previous preview
        handleCancelAudio();
        // Clear text input when starting voice recording
        setMessageInputValueState('');
        onMessageChangeParent('');

        let stream: MediaStream | null = null;

        try {
            if (typeof MediaRecorder === 'undefined') {
                throw new Error('MediaRecorder API не поддерживается в вашем браузере. Запись аудио невозможна.');
            }

            if (!MediaRecorder.isTypeSupported('audio/webm;codecs=opus') && !MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
                toast.error('Ваш браузер не поддерживает запись аудио в форматах webm/opus или ogg/opus.');
                return;
            }

            const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                ? 'audio/webm;codecs=opus'
                : 'audio/ogg;codecs=opus';

            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream, { mimeType });
            mediaRecorderRef.current = recorder;
            audioChunksRef.current = [];

            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            recorder.onstop = () => {
                const durationMs = recordingStartedAtRef.current === null
                    ? 0
                    : Date.now() - recordingStartedAtRef.current;
                const recordedAudioBlob = new Blob(audioChunksRef.current, { type: recorder.mimeType || mimeType });
                const recordingError = getAudioRecordingError(recordedAudioBlob.size, durationMs);

                mediaRecorderRef.current = null;
                recordingStartedAtRef.current = null;
                recordingStartPendingRef.current = false;
                stopRecordingAfterStartRef.current = false;
                setIsRecording(false);
                stream?.getTracks().forEach(track => track.stop());

                if (recordingError) {
                    setAudioBlob(null);
                    setAudioPreviewUrl(null);
                    audioChunksRef.current = [];
                    toast.info(recordingError);
                    return;
                }

                const audioUrl = URL.createObjectURL(recordedAudioBlob);
                setAudioBlob(recordedAudioBlob);
                setAudioPreviewUrl(audioUrl);
                console.log('Recording stopped, preview generated.');
            };

            recorder.start(250);
            recordingStartedAtRef.current = Date.now();
            recordingStartPendingRef.current = false;
            setIsRecording(true);
            console.log('Recording started...');

            if (stopRecordingAfterStartRef.current) {
                recorder.stop();
            }

        } catch (err) {
            console.error("Error accessing microphone:", err);
            const errorMessage = err instanceof Error && err.message.startsWith('MediaRecorder API')
                ? err.message
                : 'Не удалось получить доступ к микрофону. Проверьте разрешения.';
            toast.error(errorMessage);
            stream?.getTracks().forEach(track => track.stop());
            mediaRecorderRef.current = null;
            setIsRecording(false); // Ensure recording state is reset on error
        } finally {
            if (mediaRecorderRef.current?.state !== 'recording') {
                recordingStartPendingRef.current = false;
            }
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
            mediaRecorderRef.current.stop();
            // Stream tracks are stopped in onstop handler
            console.log("Stopping recording...");
        }
        // No need to set isRecording false here, it's done in onstop
    };

    // Pointer events avoid duplicate touch and compatibility mouse events on mobile browsers.
    const handleMicButtonPress = (event: React.PointerEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        startRecording();
    };

    const handleMicButtonRelease = (event: React.PointerEvent<HTMLButtonElement>) => {
        event.preventDefault();
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }

        if (recordingStartPendingRef.current) {
            stopRecordingAfterStartRef.current = true;
            return;
        }

        stopRecording();
    };


    // Helper to format currency (adjust as needed)
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'KZT' }).format(amount); // Example: KZT
    };

    // Helper to format date (adjust as needed)
    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } catch (e) {
            return '-';
        }
    };

    // Handler for clicking on a quoted message preview
    const handleQuotedMessageClick = (messageId: string) => {
        console.log(`[ChatWindow] Quoted message clicked: ${messageId}`);
        const targetMessage = messages.find(m => m.id === messageId);
        if (targetMessage) {
            const messageElement = document.getElementById(`message-item-${messageId}`);
            if (messageElement) {
                messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // Add a visual highlight temporarily
                messageElement.classList.add('bg-yellow-200', 'dark:bg-yellow-700', 'transition-all', 'duration-1000');
                setTimeout(() => {
                    messageElement.classList.remove('bg-yellow-200', 'dark:bg-yellow-700');
                }, 2000); // Remove highlight after 2 seconds
            } else {
                console.warn(`[ChatWindow] Message element ${messageId} not found in DOM for scrolling, though message data exists.`);
                // This might happen if the message is outside the currently rendered viewport of a virtualized list
                // For now, we rely on the parent to load more if needed.
            }
        } else {
            console.log(`[ChatWindow] Quoted message ${messageId} not in current view, requesting full history load and scroll.`);
            onLoadAllHistoryAndScroll(messageId);
        }
    };

    const getHeaderInfo = () => {
        if (presenceStatus === 'composing') return 'печатает...';
        if (presenceStatus === 'recording') return 'записывает аудио...';
        if (presenceStatus === 'available') return 'В сети';

        // Only format lastSeen if it's a valid positive number (timestamp in seconds)
        if (lastSeen && typeof lastSeen === 'number' && lastSeen > 0) {
            return formatLastSeen(lastSeen);
        }

        // Default to Offline if no other status applies and lastSeen is not valid
        return 'Не в сети';
    };

    return (
        <div className="h-dvh max-h-dvh w-full flex flex-col overflow-hidden"> {/* Reverted h-screen back to h-full */}
            {/* Image Viewer Modal */}
            <ImageViewer
                isOpen={imageViewerOpen}
                onClose={() => setImageViewerOpen(false)}
                imageUrl={viewerImageUrl}
                caption={viewerImageCaption}
                filename={viewerImageFilename}
            />

            {/* Forward Message Modal */}
            {forwardingMessage && (
                <ForwardMessageModal
                    isOpen={isForwardModalOpen}
                    onClose={() => {
                        setIsForwardModalOpen(false);
                        setForwardingMessage(null); // Clear message when closing modal
                    }}
                    onConfirmForward={handleConfirmForward}
                    chats={chats} // Pass the list of chats
                    currentChatId={activeChat.id} // Pass current chat ID to exclude it
                />
            )}

            {/* Header */}
            <div className="border-b border-gray-300 dark:border-gray-700" style={{ flexShrink: 0 }}>
                <ConversationHeader>
                    <Avatar src={activeChat.profilePicUrl || PLACEHOLDER_AVATAR_SVG} name={activeChat.name} />
                    <ConversationHeader.Content
                        userName={`${activeChat.contactName ? activeChat.contactName : activeChat.name} (${activeChat.phone})`}
                        info={getHeaderInfo()}
                    />
                    <ConversationHeader.Actions>
                        {/* Add any additional actions here if needed */}
                        <span>{activeChat.name ? activeChat.name : ''}</span>
                        {/* Mobile Sidebar Toggle Button */}
                        <button
                            onClick={toggleMobileSidebarVisibility}
                            className="md:hidden p-2 mr-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white focus:outline-none"
                            aria-label="Показать чаты"
                        >
                            <Bars3Icon className="h-6 w-6 text-green-500" />
                        </button>
                    </ConversationHeader.Actions>
                    {/* Add Actions like call, info etc. here if needed */}
                </ConversationHeader>
            </div>

            {/* Client Stats Info Box - Conditionally Rendered and Collapsible */}
            {activeChat.isKnownClient && (
                <div className={`bg-blue-50 border-b border-blue-200 text-xs text-gray-700 transition-all duration-300 ease-in-out overflow-hidden ${isStatsCollapsed ? 'max-h-10' : 'max-h-96'}`} style={{ flexShrink: 0 }}> {/* Added flex-shrink: 0 */}
                    <div className="flex justify-between items-center p-2 cursor-pointer" onClick={toggleStatsCollapse}>
                        <span className="font-semibold">Статистика клиента(ов)</span>
                        <button className="focus:outline-none">
                            {isStatsCollapsed ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronUpIcon className="h-4 w-4" />}
                        </button>
                    </div>
                    {!isStatsCollapsed && ( // Conditionally render the details
                        <div className="p-3 pt-0 space-y-3"> {/* Adjusted padding */}
                            {isLoadingStats && <p>Загрузка...</p>}
                            {!isLoadingStats && activeClientStatsMap && Object.keys(activeClientStatsMap).length > 0 && (
                                Object.entries(activeClientStatsMap).map(([clientId, stats]) => (
                                    <div key={clientId} className="border border-blue-100 p-2 rounded bg-white shadow-sm">
                                        <h4 className="font-semibold text-sm mb-1">
                                            <Link to={`/clients/${clientId}`} target="_client" className="text-blue-700 hover:underline">
                                                {stats.name || `Клиент ID: ${clientId}`}
                                            </Link>
                                        </h4>
                                        {stats.error ? (
                                            <p className="text-red-600">{stats.error}</p>
                                        ) : (
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                                                <div><strong>Всего заказов:</strong> {stats.totalOrderCount ?? '-'}</div>
                                                <div><strong>Активных:</strong> {stats.activeOrderCount ?? '-'}</div>
                                                <div><strong>Оплачено:</strong> {formatCurrency(stats.totalPaid ?? 0)}</div>
                                                <div><strong>Долг:</strong> <span className={(stats.totalOwed ?? 0) > 0 ? 'text-red-600 font-semibold' : ''}>{formatCurrency(stats.totalOwed ?? 0)}</span></div>
                                                <div><strong>Дата. окончания:</strong> {formatDate(stats.latestCompletionDate)}</div>
                                                <div><strong>КП:</strong> {stats.quoteCount ?? '-'}</div>
                                                {/* Display Responsible Persons list */}
                                                <div className="col-span-2 md:col-span-1"><strong>Ответственные:</strong> {stats.responsiblePersons && stats.responsiblePersons.length > 0 ? stats.responsiblePersons.join(', ') : '-'}</div>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                            {!isLoadingStats && (!activeClientStatsMap || Object.keys(activeClientStatsMap).length === 0) && <p>Не удалось загрузить статистику.</p>}
                        </div>
                    )}
                </div>
            )}

            {/* Message List - Restore standard flex scroll container and add explicit height to MessageList */}
            <div ref={messageListRef} className="flex-1 overflow-y-auto min-h-0"> {/* Restored overflow-y-auto and min-h-0 */}
                <MessageList
                    className="h-dvh" // Removed h-full here */
                    style={{ backgroundColor: 'white' }} // Removed height: 100%
                    // Remove onScroll prop
                    loadingMore={isLoadingHistory}
                /* loadingMoreText="Загрузка..." */
                >
                    {messages.length === 0 && !isLoadingHistory && (
                        <div className="p-4 text-center text-gray-500">Нет сообщений.</div>
                    )}
                    {messages.flatMap((msg, index, arr) => {
                        const prevMsg = arr[index - 1];
                        const nextMsg = arr[index + 1];
                        const position = getMessagePosition(msg, prevMsg, nextMsg);
                        const showSeparator = !prevMsg || isDifferentDay(prevMsg.timestamp, msg.timestamp);

                        const messageElements = [];

                        if (showSeparator) {
                            messageElements.push(
                                <MessageSeparator key={`sep-${msg.id || index}`}>
                                    {new Date(msg.timestamp).toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                </MessageSeparator>
                            );
                        }

                        // Group notifications should be rendered differently
                        if (msg.type === 'group_notification') {
                            messageElements.push(
                                <div id={`message-item-${msg.id}`} key={msg.id || index} className="flex justify-center">
                                    {/* Group notifications typically don't need media requests */}
                                    {renderMessageContent(msg, () => { console.warn("onRequestMedia called for group notification") }, handleImageView, handleQuotedMessageClick)}
                                </div>
                            );
                        } else {
                            // Wrapper for hover effect and reply button
                            messageElements.push(
                                <div
                                    id={`message-item-${msg.id}`} // Add ID for scrolling
                                    key={msg.id || index}
                                    className="relative group" // Add relative positioning and group for hover
                                    onMouseEnter={() => setHoveredMessageId(msg.id)}
                                    onMouseLeave={() => setHoveredMessageId(null)}
                                >
                                    <Message
                                        // key is now on the outer div
                                        model={{
                                            message: msg.message, // Keep raw message for model if needed
                                            sentTime: new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
                                            sender: msg.sender,
                                            direction: msg.direction,
                                            position: position,
                                        }}
                                    >
                                        {/* {msg.direction === 'incoming' && (position === 'first' || position === 'single') && (
                                            <Avatar src={msg.profilePicUrl || PLACEHOLDER_AVATAR_SVG} name={msg.sender} size="sm" />
                                        )} */}
                                        <Message.CustomContent>
                                            {/* Pass onRequestMedia, handleImageView, and handleQuotedMessageClick down to renderMessageContent */}
                                            {renderMessageContent(msg, onRequestMedia, handleImageView, handleQuotedMessageClick, contactHandlers)}
                                        </Message.CustomContent>
                                        {/* Footer for Incoming Messages */}
                                        {msg.direction === 'incoming' && (
                                            <Message.Footer>
                                                {/* Container to align time to the right */}
                                                <div className="flex items-center justify-end gap-1 w-full"> {/* Added w-full */}
                                                    <ArchiveOnlyBadge message={msg} />
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                                                    </span>
                                                </div>
                                            </Message.Footer>
                                        )}
                                        {/* Footer for Outgoing Messages */}
                                        {msg.direction === 'outgoing' && (
                                            <Message.Footer>
                                                {/* Container for time and status, aligned to the right */}
                                                <div className="flex items-center justify-end space-x-1 w-full">
                                                    <ArchiveOnlyBadge message={msg} />
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                                                    </span>
                                                    <MessageStatusIndicator ack={msg.ack} sendStatus={msg.sendStatus} />
                                                </div>
                                            </Message.Footer>
                                        )}
                                    </Message>
                                    {/* Reply & Forward Buttons - Show on hover */}
                                    {hoveredMessageId === msg.id && !msg.id.startsWith('temp-') && ( // Don't allow replying/forwarding temp messages
                                        <> {/* Wrap buttons in a fragment */}
                                            <button
                                                onClick={() => handleInitiateReply(msg)} // Use internal handler
                                                className={`absolute bottom-1 p-1 rounded bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none z-10 ${msg.direction === 'outgoing' ? 'right-8' : 'left-1'}`} // Adjusted position: bottom, right/left
                                                aria-label="Ответить"
                                                title="Ответить"
                                            >
                                                <ArrowUturnLeftIcon className="h-4 w-4" />
                                            </button>
                                            {/* Forward Button - Show on hover, next to reply */}
                                            <button
                                                onClick={() => handleInitiateForward(msg)}
                                                className={`absolute bottom-1 p-1 rounded bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none z-10 ${msg.direction === 'outgoing' ? 'right-1' : 'left-8'}`} // Adjusted position: bottom, right/left
                                                aria-label="Переслать"
                                                title="Переслать"
                                            >
                                                <ArrowRightIcon className="h-4 w-4" />
                                            </button>
                                        </> // Close fragment
                                    )}
                                </div>
                            );
                        }
                        return messageElements;
                    })}
                </MessageList>
            </div>

            {/* Input Area Wrapper */}
            <div className="border-t border-gray-300 dark:border-gray-700" style={{ flexShrink: 0 }}>
                {/* Reply Preview Area - Rendered above input */}
                {replyingToMessage && (
                    <ReplyPreview
                        message={replyingToMessage}
                        onCancelReply={() => setReplyingToMessage(null)}
                    />
                )}
                {/* Audio Preview Area - Rendered above input when audio is recorded */}
                {audioPreviewUrl && audioBlob && (
                    <AudioPreview
                        audioUrl={audioPreviewUrl}
                        audioBlob={audioBlob} // Pass blob for potential duration calculation or sending
                        onSend={handleSendAudio}
                        onCancel={handleCancelAudio}
                    />
                )}
                {/* Actual Input Area - Conditionally render based on recording/preview state */}
                {!audioPreviewUrl && ( // Show input/mic only if there's no preview
                    <div className="p-2 flex items-end space-x-2">
                        {/* Hidden file input */}
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileSend} // Use internal handler
                            style={{ display: 'none' }}
                            accept="image/*,audio/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar"
                            multiple
                        />

                        {/* Attach Button */}
                        <button
                            onClick={onAttachClick}
                            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none relative"
                            aria-label="Прикрепить файл"
                            title={selectedFiles.length > 0 ? `Выбрано файлов: ${selectedFiles.length}` : "Прикрепить файл"}
                        >
                            <PaperClipIcon className="h-6 w-6" />
                            {selectedFiles.length > 0 && (
                                <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                    {selectedFiles.length}
                                </span>
                            )}
                        </button>

                        {/* Textarea Input */}
                        <textarea
                            ref={textareaRef}
                            placeholder="Введите сообщение..."
                            value={messageInputValue}
                            onChange={handleChange}
                            onPaste={handlePaste} // Add paste handler
                            onKeyDown={handleKeyDown} // Add keydown handler
                            rows={1} // Start with one row
                            className="flex-grow p-2 border border-gray-300 dark:border-gray-600 rounded-md resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            style={{ overflowY: 'hidden' }} // Hide scrollbar initially
                            disabled={isRecording} // Disable textarea while recording
                        />

                        {/* Conditional Button: Send or Mic */}
                        {messageInputValue.trim() && !isRecording ? (
                            // Send Button
                            <button
                                onClick={handleSendText} // Use text send handler
                                className={`p-2 rounded-md focus:outline-none bg-blue-500 text-white hover:bg-blue-600`}
                                aria-label="Отправить сообщение"
                            >
                                <PaperAirplaneIcon className="h-6 w-6" />
                            </button>
                        ) : (
                            // Microphone Button
                            <button
                                onPointerDown={handleMicButtonPress}
                                onPointerUp={handleMicButtonRelease}
                                onPointerCancel={handleMicButtonRelease}
                                className={`p-2 rounded-full focus:outline-none ${isRecording ? 'bg-red-500 text-white animate-pulse' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'}`}
                                style={{ touchAction: 'none' }}
                                aria-label={isRecording ? "Остановить запись" : "Начать запись голоса"}
                            >
                                <MicrophoneIcon className="h-6 w-6" />
                            </button>
                        )}
                        {/* Old Send Button Logic (commented out/removed)
                    <button
                        onClick={handleSendText} // Use text send handler
                        disabled={!messageInputValue.trim() || isRecording} // Disable if no text or recording
                        className={`p-2 rounded-md focus:outline-none ${messageInputValue.trim() && !isRecording
                            ? 'bg-blue-500 text-white hover:bg-blue-600'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400' // Keep disabled style logic
                            }`}
                        aria-label="Отправить сообщение"
                    >
                        <PaperAirplaneIcon className="h-6 w-6" />
                    </button>
                    */}
                    </div>
                )} {/* End conditional rendering for input area */}
            </div> {/* Close the outer div wrapper for input area */}
        </div>
    );
};

export default ChatWindow;
